import * as Sentry from '@sentry/vue'
import {
  CompositeBatchFailResult,
  compositeRetrieve,
  getAuthorizationUrl,
  requestAccessToken,
  Rest,
  setDefaultConfig,
  StandardRestError
} from 'ts-force'
import {
  Action,
  Module,
  Mutation,
  MutationAction,
  VuexModule
} from 'vuex-module-decorators'

import { Account, Order, Proof } from '@/generated'
import store from '@/store'
import { OAuthCallback } from '@/store/modules/types/salesforce'
import createAuthRefreshInterceptor from '@/utils/axios-auth-refresh'
import { isAllowedOrigin, isLocalhost } from '@/utils/origin'

// Using the Experience Cloud community URL so both internal and community users can authenticate.
// Previously used pc.my.salesforce.com (My Domain), but community users cannot OAuth against that endpoint.
export const SALESFORCE_INSTANCE_URL = 'https://pc.my.site.com'
// Hashi-specific
export const SALESFORCE_CLIENT_ID =
  '3MVG9yZ.WNe6byQB1SRML81XKQya.q8od0ghDZoZNHGIRQZ7wv9tfgYgq2m.H0khPjRob4Eyo.6PtkhljrR3h'

/**
 * Constructs an absolute URL to Salesforce production Lightning UI from a Salesforce ID.
 * This URL avoids a few redirects and improves performance compared to linking
 * to `https://pc.my.salesforce.com/{$id}`.
 */
export const salesforceUrl = (salesforceId: string): string =>
  `https://pc.lightning.force.com/lightning/_classic/%2F${salesforceId}`

/**
 * Normalize a US phone number to 10 digits.
 * Accepts: 5551234567, 15551234567, +15551234567, (555) 123-4567, etc.
 * Strips a leading `1` from 11-digit inputs (US country code).
 * Returns null if the input isn't a valid US phone number.
 *
 * Note: bare 11-digit inputs like 15061200123 normalize as phone here,
 * but detectFormat() checks order numbers first, so ambiguous inputs
 * are resolved by the detection priority, not by this function.
 */
export function normalizeUSPhone(input: string): string | null {
  const digits = input.replace(/\D/g, '')
  if (digits.length === 10) return digits
  if (digits.length === 11 && digits[0] === '1') return digits.slice(1)
  return null
}

/**
 * Build a SOQL LIKE pattern that matches a 10-digit US phone number
 * regardless of how it's stored (raw digits, dashes, parens, +1 prefix, etc).
 * Trailing % handles dirty data: invisible Unicode chars, stray letters,
 * appended text like "(USA)", etc.
 *
 * '%540%279%8456%' matches: 5402798456, (540) 279-8456, +15402798456,
 * ‪(540) 279-8456‬ (with Unicode LTR marks), 540-279-8456 (USA), etc.
 */
export function phoneLikePattern(digits10: string): string {
  const area = digits10.slice(0, 3)
  const prefix = digits10.slice(3, 6)
  const suffix = digits10.slice(6, 10)
  return `%${area}%${prefix}%${suffix}%`
}

export interface AccountMatch {
  email: string
  name: string
  id: string
  /** Context about how the match was found (e.g. matched proof ID) */
  matchContext?: string
  /** The proof ID or order number that matched, for UI highlighting */
  matchedProofId?: string
  matchedOrderNumber?: string
}

const SALESFORCE_ACCESS_TOKEN_KEY = 'salesforceAccessToken'
const SALESFORCE_REFRESH_TOKEN_KEY = 'salesforceRefreshToken'
const SALESFORCE_USER_INSTANCE_URL_KEY = 'salesforceUserInstanceUrl'

const name = 'salesforce'
if (module.hot && store.hasModule(name)) {
  store.unregisterModule(name)
}

@Module({ name, namespaced: true, dynamic: true, store })
export default class Salesforce extends VuexModule {
  // TODO: For all fetched data, consider creating custom interfaces based on each SOQL query and object schema
  //  to remove `null` where not expected, and to remove fields that don't exist in the query.
  account: Account | null = null

  proofs: Proof[] = []

  orders: Order[] = []

  accessToken: string | null = null

  /** The instance URL returned from the OAuth flow, indicating the user's Salesforce environment */
  userInstanceUrl: string | null = localStorage.getItem(
    SALESFORCE_USER_INSTANCE_URL_KEY
  )

  refreshToken: string | null = localStorage.getItem(
    SALESFORCE_REFRESH_TOKEN_KEY
  )

  /** Error messages from the last fetchAll, or null if successful */
  fetchErrors: string[] | null = null

  // TODO: Refactor repository to normalize data for efficient lookups with vuex-orm
  get proofByProofId() {
    return (proofId: string): Proof | null => {
      return this.proofs.find((proof) => proof.proofId === proofId) || null
    }
  }

  // TODO: Refactor repository to normalize data for efficient lookups with vuex-orm
  get proofBySalesforceId() {
    return (salesforceId: string): Proof | null => {
      return this.proofs.find((proof) => proof.id === salesforceId) || null
    }
  }

  @Mutation
  setDemoData(data: {
    account: Account | null
    proofs: Proof[]
    orders: Order[]
  }): void {
    this.account = data.account
    this.proofs = data.proofs
    this.orders = data.orders
    this.fetchErrors = null
  }

  @Mutation
  setAccessToken(accessToken: string | null): void {
    if (accessToken !== null) {
      localStorage.setItem(SALESFORCE_ACCESS_TOKEN_KEY, accessToken)
      setDefaultConfig({
        accessToken,
        instanceUrl: SALESFORCE_INSTANCE_URL
      })
    } else {
      localStorage.removeItem(SALESFORCE_ACCESS_TOKEN_KEY)
    }
    this.accessToken = accessToken
  }

  @Mutation
  setRefreshToken(refreshToken: string | null): void {
    if (refreshToken !== null) {
      localStorage.setItem(SALESFORCE_REFRESH_TOKEN_KEY, refreshToken)
    } else {
      localStorage.removeItem(SALESFORCE_REFRESH_TOKEN_KEY)
    }
    this.refreshToken = refreshToken
  }

  @Mutation
  setUserInstanceUrl(instanceUrl: string | null): void {
    if (instanceUrl !== null) {
      localStorage.setItem(SALESFORCE_USER_INSTANCE_URL_KEY, instanceUrl)
    } else {
      localStorage.removeItem(SALESFORCE_USER_INSTANCE_URL_KEY)
    }
    this.userInstanceUrl = instanceUrl
  }

  @Action({ rawError: true })
  async setAccessTokenAndConfigureTokenRefresh(
    accessToken: string
  ): Promise<void> {
    const hasExistingAccessToken = !!this.accessToken

    // Manually commit the mutation first so that default config is initialized before
    // the interceptor is set up, otherwise the interceptor won't work properly.
    // This also allows us to skip running the mutation if there's no access token,
    // whereas if we use the annotation, it will run every time.
    this.context.commit('setAccessToken', accessToken)

    // Set up access token refresh, but only once so that duplicate interceptors aren't registered
    if (!hasExistingAccessToken) {
      // Get default instance to configure it with interceptor
      const restInstance = new Rest()

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const refreshAuthLogic = async (failedRequest: any) => {
        await this.refreshAccessToken()
        // After refresh, this.accessToken has the new value
        const newAuthHeader = `Bearer ${this.accessToken}`
        failedRequest.response.config.headers['Authorization'] = newAuthHeader
        // Also update our instance so any additional request use the new token
        restInstance.request.defaults.headers['Authorization'] = newAuthHeader
      }

      // Intercept 401 responses and silently refresh the access token and retry
      createAuthRefreshInterceptor(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ts-force uses axios 0.x types
        restInstance.request as any,
        refreshAuthLogic,
        {
          // CAUTION: This will attempt to refresh the access token on any network failure
          // because Salesforce doesn't return CORS headers on a 401 Unauthorized response,
          // so we can't differentiate between a loss of connectivity to Salesforce and
          // an invalid access token. Despite being coarse, it should work as intended in most
          // scenarios.
          interceptNetworkError: true
        }
      )
    }
  }

  @Action({ rawError: true })
  async loadAccessToken(): Promise<string | null> {
    const accessToken = localStorage.getItem(SALESFORCE_ACCESS_TOKEN_KEY)
    if (accessToken) {
      await this.setAccessTokenAndConfigureTokenRefresh(accessToken)
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Loaded Salesforce access token from local storage'
      })
    }
    return accessToken
  }

  /**
   * Returns a Promise that's resolved once the new access token is fetched.
   * MUST be invoked in a click handler, otherwise the popup will be blocked.
   */
  @Action({ rawError: true })
  getAccessToken(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Login must occur in a new window because it cannot be iframed due to Salesforce
      // security settings that prevent clickjacking.
      const popup = window.open(
        getAuthorizationUrl({
          response_type: 'code',
          instanceUrl: SALESFORCE_INSTANCE_URL,
          client_id: SALESFORCE_CLIENT_ID,
          redirect_uri:
            (isLocalhost()
              ? // Salesforce OAuth redirect URIs are registered for localhost
                // ports 8095-8099 (http + https) to support parallel dev sessions.
                location.origin
              : // Production callback URL is used because Workers preview URLs are dynamic.
                // The callback page will use postMessage to send tokens back to the opener
                // after validating the origin.
                'https://hashi.paperculture.com') + '/oauth-callback',
          // Doesn't seem to make a difference, but it can't hurt in case things change
          display: 'popup',
          // Callback URL will use origin for postMessage security
          state: JSON.stringify({
            origin: location.origin
          })
        }),
        '_blank',
        'width=500,height=600'
      )

      if (!popup) {
        reject(
          new Error(
            'Popup was blocked by the browser. Please allow popups for this site and try again.'
          )
        )
        return
      }

      const popupClosedPoller = setInterval(() => {
        if (popup?.closed) {
          clearInterval(popupClosedPoller)
          reject(new Error('Login canceled by user'))

          window.removeEventListener('message', setTokens)
        }
      }, 500)

      const setTokens = async (event: MessageEvent) => {
        if (!isAllowedOrigin(event.origin)) {
          // Ignore message and keep listening for the one we expect
          return
        }

        // Stop checking to see if the login was canceled
        clearInterval(popupClosedPoller)
        // We only expect to get one message
        window.removeEventListener('message', setTokens)

        if (typeof event.data == 'object') {
          const data = event.data as OAuthCallback
          if (data.accessToken && data.refreshToken) {
            await this.setAccessTokenAndConfigureTokenRefresh(data.accessToken)
            this.setRefreshToken(data.refreshToken)
            if (data.instanceUrl) {
              this.setUserInstanceUrl(data.instanceUrl)
            }
            resolve()
          } else {
            const error =
              'Access token and/or refresh token not present in OAuthCallback'
            console.error(error, data)
            reject(new Error(error))
          }
        } else {
          // Don't reject in case this message came from something unexpected, like an extension
          console.error('Object expected for OAuthCallback data', event.data)
        }
      }
      window.addEventListener('message', setTokens)
    })
  }

  /**
   * Uses the refresh token to get a new access token.
   *
   * If there's a problem, all tokens will be cleared to force the user to log back in.
   */
  @Action({ rawError: true })
  async refreshAccessToken(): Promise<void> {
    if (this.refreshToken) {
      try {
        const response = await requestAccessToken({
          grant_type: 'refresh_token',
          instanceUrl: SALESFORCE_INSTANCE_URL,
          client_id: SALESFORCE_CLIENT_ID,
          refresh_token: this.refreshToken
        })
        this.setAccessToken(response.access_token)
      } catch (e: unknown) {
        // Distinguish permanent failures (revoked token) from transient ones (network).
        // AxiosError.message is generic ("Request failed with status code 400"),
        // so we must also check the Salesforce response body for error codes.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const responseData = (e as any)?.response?.data
        const errorText =
          (e instanceof Error ? e.message : '') +
          ' ' +
          (typeof responseData === 'string'
            ? responseData
            : typeof responseData?.error === 'string'
              ? responseData.error
              : '') +
          ' ' +
          (typeof responseData?.error_description === 'string'
            ? responseData.error_description
            : '')
        const isRevoked = /invalid_grant|expired|INVALID_SESSION_ID/.test(
          errorText
        )
        if (isRevoked) {
          console.error('Salesforce token revoked, signing out:', e)
          Sentry.captureMessage('Salesforce refresh token revoked', {
            level: 'warning'
          })
          this.clearTokens()
        } else {
          // Transient failure (network, timeout) — don't sign out,
          // let the next request retry via the auth interceptor
          console.error('Transient error refreshing Salesforce token:', e)
          Sentry.captureException(e, {
            tags: { action: 'refreshAccessToken' }
          })
        }
        // Re-throw so the auth refresh interceptor knows the refresh failed
        // and rejects the original request instead of retrying with bad/no credentials
        throw e
      }
    } else {
      throw new Error(
        'Cannot refresh access token because no refresh token was found'
      )
    }
  }

  /**
   * Delete Salesforce tokens from the store and local storage, which will effectively sign the user out of
   * Salesforce, in Hashi. The tokens themselves will not be invalidated.
   */
  @Action({ rawError: true })
  clearTokens(): void {
    this.setAccessToken(null)
    this.setRefreshToken(null)
    this.setUserInstanceUrl(null)
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Cleared Salesforce access tokens'
    })
  }

  /**
   * Look up customers by US phone number via Account.PersonOtherPhone.
   * Returns all matches sorted by LastModifiedDate (most recent first).
   */
  @Action({ rawError: true })
  async lookupByPhone(phone: string): Promise<AccountMatch[]> {
    const normalized = normalizeUSPhone(phone)
    if (!normalized) return []
    try {
      const pattern = phoneLikePattern(normalized)
      const accounts = await Account.retrieve(
        `SELECT Id, LoginName__c, FirstName, LastName FROM Account` +
          ` WHERE PersonOtherPhone LIKE '${pattern}'` +
          ` ORDER BY LastModifiedDate DESC` +
          ` LIMIT 10`
      )
      return accounts
        .filter((a) => a.loginName)
        .map((a) => ({
          email: a.loginName as string,
          name: [a.firstName, a.lastName].filter(Boolean).join(' '),
          id: a.id as string
        }))
    } catch (e: unknown) {
      console.error('[salesforce] lookupByPhone failed:', e)
      Sentry.captureException(e, { tags: { action: 'lookupByPhone' } })
      return []
    }
  }

  /**
   * Fall back to looking up customers by phone number on recent Orders.
   * Used when Account.PersonOtherPhone has no matches (phone not yet synced).
   */
  @Action({ rawError: true })
  async lookupByPhoneViaOrders(phone: string): Promise<AccountMatch[]> {
    const normalized = normalizeUSPhone(phone)
    if (!normalized) return []
    try {
      const pattern = phoneLikePattern(normalized)
      const orders = await Order.retrieve(
        `SELECT Customer__r.Id, Customer__r.LoginName__c,` +
          ` Customer__r.FirstName, Customer__r.LastName FROM Order__c` +
          ` WHERE PhoneNumber__c LIKE '${pattern}'` +
          ` ORDER BY CreatedDate DESC LIMIT 10`
      )
      const seen = new Set<string>()
      return orders.reduce<AccountMatch[]>((matches, o) => {
        const cust = o.customer
        const email = cust?.loginName
        if (email && !seen.has(email)) {
          seen.add(email)
          matches.push({
            email,
            name: [cust.firstName, cust.lastName].filter(Boolean).join(' '),
            id: cust.id || ''
          })
        }
        return matches
      }, [])
    } catch (e: unknown) {
      console.error('[salesforce] lookupByPhoneViaOrders failed:', e)
      Sentry.captureException(e, { tags: { action: 'lookupByPhoneViaOrders' } })
      return []
    }
  }

  /**
   * Look up a customer by order number (prefix match, deduplicated by customer).
   */
  @Action({ rawError: true })
  async lookupByOrderNumber(orderNumber: string): Promise<AccountMatch[]> {
    // Accepts a pre-normalized order number (Avetti digits or Mercury alphanumeric).
    // Callers should use normalizeOrderNumber() from searchDetection.ts first.
    if (orderNumber.length !== 11) return []
    const queryValue = orderNumber.replace(/'/g, "\\'")
    try {
      const orders = await Order.retrieve(
        `SELECT Customer__r.Id, Customer__r.LoginName__c,` +
          ` Customer__r.FirstName, Customer__r.LastName FROM Order__c` +
          ` WHERE OrderNumber__c = '${queryValue}'` +
          ` LIMIT 1`
      )
      const seen = new Set<string>()
      return orders.reduce<AccountMatch[]>((matches, o) => {
        const cust = o.customer
        const email = cust?.loginName
        if (email && !seen.has(email)) {
          seen.add(email)
          matches.push({
            email,
            name: [cust.firstName, cust.lastName].filter(Boolean).join(' '),
            id: cust.id || '',
            matchedOrderNumber: orderNumber
          })
        }
        return matches
      }, [])
    } catch (e: unknown) {
      console.error('[salesforce] lookupByOrderNumber failed:', e)
      Sentry.captureException(e, { tags: { action: 'lookupByOrderNumber' } })
      return []
    }
  }

  @Action({ rawError: true })
  async lookupByProofId(prefix: string): Promise<AccountMatch[]> {
    if (prefix.length < 3) return []
    try {
      // Escape SOQL special chars to prevent injection and unintended wildcards
      const safePrefix = prefix
        .replace(/'/g, "\\'")
        .replace(/%/g, '\\%')
        .replace(/_/g, '\\_')
      // TODO: ProofId__c is case-sensitive (External ID). Add a formula field
      // like ProofId_Lower__c = LOWER(ProofId__c) and query that instead to
      // support case-insensitive prefix search.
      const proofs = await Proof.retrieve(
        `SELECT ProofId__c, Project__r.ProjectId__c, Name, CreatedDate,` +
          ` Customer__r.Id, Customer__r.LoginName__c,` +
          ` Customer__r.FirstName, Customer__r.LastName FROM Proof__c` +
          ` WHERE ProofId__c LIKE '${safePrefix}%'` +
          ` ORDER BY LastModifiedDate DESC LIMIT 20`
      )
      // Filter to main proofs only (case-sensitive match already handled by SOQL)
      const seen = new Set<string>()
      return proofs
        .filter((p) => p.proofId === p.project?.projectId)
        .reduce<AccountMatch[]>((matches, p) => {
          const cust = p.customer
          const email = cust?.loginName
          if (email && !seen.has(email)) {
            seen.add(email)
            const proofId = (p.proofId || '').substring(0, 8)
            const proofName = p.name || ''
            matches.push({
              email,
              name: [cust.firstName, cust.lastName].filter(Boolean).join(' '),
              id: cust.id || '',
              matchContext: `${proofId}… ${proofName}`,
              matchedProofId: p.proofId || undefined
            })
          }
          return matches
        }, [])
    } catch (e: unknown) {
      console.error('[salesforce] lookupByProofId failed:', e)
      Sentry.captureException(e, { tags: { action: 'lookupByProofId' } })
      return []
    }
  }

  @MutationAction({
    mutate: ['account', 'proofs', 'orders', 'fetchErrors'],
    rawError: true
  })
  async fetchAll(email: string): Promise<{
    proofs: Proof[]
    orders: Order[]
    account: Account | null
    fetchErrors: string[] | null
  }> {
    const MAX_RETRIES = 2
    const doQuery = () =>
      compositeRetrieve(Account, Proof, Order)(
        (account) => ({
          select: [
            ...account.select(
              'id',
              'firstName',
              'lastName',
              'loginName',
              'clientId',
              'timeZone',
              'weddingDate',
              'emailCollectionDate',
              'registrationDate',
              'vip',
              'amountSpent',
              'orderCount',
              'personOtherPhone',
              'personHasOptedOutOfEmail'
            ),
            account.subQuery('negatives', (negative) => {
              return {
                select: [
                  ...negative.select(
                    'id',
                    'name',
                    'orderId',
                    'negativeResponsibility',
                    'category',
                    'zendeskTicketUrl',
                    'description',
                    'resolutionDescription',
                    'shippingUpgrade',
                    'reprinted',
                    'creditGiven',
                    'refundGiven',
                    'createdDate'
                  ),
                  ...negative.parent('createdBy').select('id', 'name', 'email')
                ],
                orderBy: {
                  field: negative.select('createdDate'),
                  order: 'DESC'
                }
              }
            }),
            account.subQuery(
              'customerSpecialInstructions',
              (specialInstruction) => {
                return {
                  select: [
                    ...specialInstruction.select(
                      'id',
                      'name',
                      'type',
                      'description',
                      'archived',
                      'createdDate'
                    ),
                    ...specialInstruction
                      .parent('createdBy')
                      .select('id', 'name', 'email')
                  ],
                  orderBy: {
                    field: specialInstruction.select('createdDate'),
                    order: 'DESC'
                  }
                }
              }
            ),
            account.subQuery('customerPromotions', (customerPromotion) => {
              return {
                select: customerPromotion.select(
                  'id',
                  'name',
                  'effectiveStartTime',
                  'effectiveEndTime',
                  'isActive',
                  'remainingUsesCount'
                ),
                orderBy: {
                  field: customerPromotion.select('createdDate'),
                  order: 'DESC'
                }
              }
            }),
            account.subQuery('delightedResponses', (delightedResponse) => {
              return {
                select: delightedResponse.select(
                  'score',
                  'comment',
                  'properties',
                  'timestamp'
                ),
                orderBy: {
                  field: delightedResponse.select('timestamp'),
                  order: 'DESC'
                }
              }
            }),
            account.subQuery('sampleRequests', (sampleRequest) => {
              return {
                select: sampleRequest.select(
                  'id',
                  'type',
                  'isFulfillable',
                  'fulfillmentDate',
                  'eventDate',
                  'eventStyle',
                  'guestCount',
                  'firstName',
                  'lastName',
                  'address1',
                  'address2',
                  'address3',
                  'city',
                  'state',
                  'zipCode',
                  'phone',
                  'createdDate'
                ),
                orderBy: {
                  field: sampleRequest.select('createdDate'),
                  order: 'DESC'
                }
              }
            })
          ],
          where: [
            {
              field: account.select('loginName'),
              op: '=',
              val: email
            }
          ],
          limit: 1
        }),
        (proof) => ({
          select: [
            ...proof.select(
              'id',
              'name',
              'proofId',
              'formatName',
              'canonicalCategoryName',
              'orderItemId',
              'express',
              'muteEmailReminders',
              'paid',
              'status',
              'createdDate',
              'lastModifiedDate',
              'dateFirstApproved',
              'approvalTime',
              'dateOrderItemCreated',
              'readyForPrintTime',
              'shipDate',
              'bestRevisionId',
              'lastModifiedDate',
              // TODO: Now that Item__c is accessible, fetch via proof.parent('product').select('itemId', 'itemCode')
              'itemId',
              'code'
            ),
            ...proof.parent('baseProof').select('id', 'proofId'),
            proof.parent('recordType').select('name'),
            proof.parent('project').select('projectId'),
            ...proof.parent('firstDesigner').select('id', 'name', 'email'),
            proof.subQuery('revisions', (revision) => {
              return {
                select: [
                  ...revision.select(
                    'id',
                    'name',
                    'status',
                    'baseRevision',
                    'revisionNumber',
                    'proofUrl',
                    'rawProofUrl',
                    'csrComments',
                    'internalComments',
                    'customerComments',
                    'toCustomerComments',
                    'toCustomerCommentsImportant',
                    'createdDate',
                    'lastModifiedDate'
                  ),
                  ...revision.parent('designer').select('id', 'name', 'email')
                ],
                orderBy: {
                  field: revision.select('revisionNumber')
                }
              }
            }),
            proof.subQuery('specialInstructions', (specialInstruction) => {
              return {
                select: [
                  ...specialInstruction.select(
                    'id',
                    'name',
                    'type',
                    'description',
                    'complete',
                    'createdDate'
                  ),
                  ...specialInstruction
                    .parent('createdBy')
                    .select('id', 'name', 'email')
                ],
                orderBy: {
                  field: specialInstruction.select('createdDate'),
                  order: 'DESC'
                }
              }
            })
          ],
          where: [
            {
              field: proof.parent('customer').select('loginName'),
              op: '=',
              val: email
            }
          ],
          orderBy: {
            field: proof.select('lastModifiedDate'),
            order: 'DESC'
          }
        }),
        (order) => ({
          select: [
            ...order.select('id', 'orderNumber', 'phoneNumber', 'createdDate'),
            order.subQuery('orderSpecialInstructions', (specialInstruction) => {
              return {
                select: [
                  ...specialInstruction.select(
                    'id',
                    'name',
                    'type',
                    'description',
                    'complete',
                    'createdDate'
                  ),
                  ...specialInstruction
                    .parent('createdBy')
                    .select('id', 'name', 'email')
                ],
                orderBy: {
                  field: specialInstruction.select('createdDate'),
                  order: 'DESC'
                }
              }
            })
          ],
          where: [
            {
              field: order.parent('customer').select('loginName'),
              op: '=',
              val: email
            }
          ],
          orderBy: {
            field: order.select('createdDate'),
            order: 'DESC'
          }
        })
      )

    // Retry with exponential backoff on network/transient failures.
    // SOQL permission errors come back as CompositeBatchFailResult (not thrown),
    // so retries only help with network-level issues.
    let result: Awaited<ReturnType<typeof doQuery>> | undefined
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        result = await doQuery()
        break
      } catch (e: unknown) {
        // Don't retry if tokens were cleared (permanent auth failure)
        if (!this.accessToken) {
          throw e
        }
        if (attempt < MAX_RETRIES) {
          const delay = 1000 * Math.pow(2, attempt)
          console.warn(
            `[salesforce] fetchAll attempt ${
              attempt + 1
            } failed, retrying in ${delay}ms:`,
            e
          )
          await new Promise((r) => setTimeout(r, delay))
        } else {
          Sentry.captureException(e, {
            tags: { action: 'fetchAll' },
            extra: { email, attempts: attempt + 1 }
          })
          throw e
        }
      }
    }
    // result is always set: the loop either breaks on success or throws on final failure
    if (!result) throw new Error('fetchAll: unexpected empty result')
    const [accounts, proofs, orders] = result

    Sentry.addBreadcrumb({
      category: 'salesforce',
      message: 'fetchAll',
      level: 'debug',
      data: {
        accounts,
        proofs,
        orders
      }
    })

    // Surface composite batch failures — these indicate SOQL/permission errors
    // that would otherwise silently show empty data.
    // Each result is either an array of records (success) or a CompositeBatchFailResult (failure).
    const accountErrors = Array.isArray(accounts)
      ? null
      : (accounts as CompositeBatchFailResult<StandardRestError[]>)
    const proofErrors = Array.isArray(proofs)
      ? null
      : (proofs as CompositeBatchFailResult<StandardRestError[]>)
    const orderErrors = Array.isArray(orders)
      ? null
      : (orders as CompositeBatchFailResult<StandardRestError[]>)

    if (accountErrors || proofErrors || orderErrors) {
      const errors = [
        ...(accountErrors?.result || []).map((e) => `Account: ${e.message}`),
        ...(proofErrors?.result || []).map((e) => `Proofs: ${e.message}`),
        ...(orderErrors?.result || []).map((e) => `Orders: ${e.message}`)
      ]
      const errorSummary = errors.join('; ')
      console.error('[salesforce] Composite batch errors:', errorSummary)
      Sentry.captureMessage('Salesforce composite batch failure', {
        level: 'error',
        extra: {
          email,
          accountErrors: accountErrors?.result,
          accountStatusCode: accountErrors?.statusCode,
          proofErrors: proofErrors?.result,
          proofStatusCode: proofErrors?.statusCode,
          orderErrors: orderErrors?.result,
          orderStatusCode: orderErrors?.statusCode
        }
      })
    }

    // Build readable error messages grouped by object.
    // INVALID_FIELD errors list the inaccessible fields; other errors show the code.
    const escapeHtml = (s: string): string =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const codeTag = (name: string): string =>
      `<code>${escapeHtml(name.trim())}</code>`
    const summarizeErrors = (
      label: string,
      errors: CompositeBatchFailResult<StandardRestError[]>
    ): string => {
      const fieldNames: string[] = []
      const otherErrors: string[] = []
      for (const e of errors.result) {
        if (e.errorCode === 'INVALID_FIELD') {
          // Field names appear before the ^ marker in the SOQL error,
          // as a comma-separated list with possible newlines
          const raw = e.message.split('^')[0]?.trim()
          if (raw) {
            raw
              .split(/[,\n]+/)
              .map((f) => f.trim())
              .filter(Boolean)
              .forEach((f) => fieldNames.push(f))
          }
        } else {
          otherErrors.push(e.errorCode || 'UNKNOWN')
        }
      }
      const parts: string[] = []
      if (fieldNames.length) {
        parts.push(
          `No access to ${label} fields: ${fieldNames.map(codeTag).join(', ')}.`
        )
      }
      if (otherErrors.length) {
        parts.push(`${label}: ${otherErrors.join(', ')}.`)
      }
      return parts.join(' ')
    }
    const userErrors: string[] = []
    if (accountErrors)
      userErrors.push(summarizeErrors('Account', accountErrors))
    if (proofErrors) userErrors.push(summarizeErrors('Proof', proofErrors))
    if (orderErrors) userErrors.push(summarizeErrors('Order', orderErrors))
    if (userErrors.length) {
      userErrors.push(
        'Ask a Salesforce admin to update field permissions, or try a different account.'
      )
    }

    return {
      // Account may not exist (empty results) or may have failed (permission errors)
      account: Array.isArray(accounts) && accounts.length ? accounts[0] : null,
      proofs: Array.isArray(proofs) ? proofs : [],
      orders: Array.isArray(orders) ? orders : [],
      fetchErrors: userErrors.length ? userErrors : null
    }
  }
}
